import type { DocPage } from './types';

/**
 * Integrations, the API surface, operational guidance and the error index.
 *
 * The audit checklist is not here — it is generated from every page's `status`
 * in `audit.ts`, so it cannot drift from what the feature pages claim.
 */
export const REFERENCE_PAGES: DocPage[] = [
  // -------------------------------------------------------------------------
  {
    slug: 'integrations',
    title: 'Integrations',
    summary: 'n8n, Make, the SDK, and plain HTTP — every way an agent can reach the gateway.',
    section: 'integrations',
    keywords: ['n8n', 'make', 'sdk', 'python', 'integration', 'connect', 'automation'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'partial',
      gaps: [
        'None of the three packages has been published yet. The npm and PyPI names below are what they will be, not what is installable today.',
        'The Make app is a definition file validated in CI; it has not been submitted to Make.',
      ],
    },
    blocks: [
      {
        kind: 'text',
        body: 'All four routes hit the same three endpoints and authenticate the same way: an x-chairly-key header carrying a gateway key.',
      },
      {
        kind: 'table',
        headers: ['Call', 'Endpoint', 'What it does'],
        rows: [
          ['request', 'POST /v1/approvals/request', 'Submit an action and block while a human decides.'],
          ['get', 'GET /v1/approvals/:approvalId', 'Poll one approval by id.'],
          ['resume', 'GET /v1/approvals/resume/:resumeToken', 'Pick a paused run back up.'],
        ],
      },

      { kind: 'heading', body: 'n8n' },
      {
        kind: 'steps',
        items: [
          'Install the community node @chairly/n8n-nodes-require-approval.',
          'Add Chairly API credentials: your gateway key and the gateway URL.',
          'Drop a Require Approval node in front of any step that sends money, email, or bulk changes.',
        ],
      },
      {
        kind: 'text',
        body: 'The node holds the workflow inside its own HTTP request while it waits, then polls. On decline or timeout it throws, so nothing downstream ever runs on an unapproved action.',
      },

      { kind: 'heading', body: 'Make' },
      {
        kind: 'steps',
        items: [
          'Profile → Organizations → Custom apps → Create, and import app.json from packages/make-app.',
          'Add your gateway key and URL as the connection.',
          'Add the Require Approval module before the risky step.',
        ],
      },

      { kind: 'heading', body: 'SDK' },
      {
        kind: 'code',
        language: 'typescript',
        body: `import { ApprovalsClient } from '@chairly/approvals';

const approvals = new ApprovalsClient({
  baseUrl: 'https://your-api-host',
  apiKey: process.env.CHAIRLY_API_KEY,
  workspaceId: 'cw_acme',
});

const ticket = await approvals.request({
  action: 'stripe.refunds.create',
  params: { charge: 'ch_123', amount: 4200 },
  waitMs: 30_000,
});`,
      },
      {
        kind: 'code',
        language: 'python',
        body: `from chairly_approvals import ApprovalsClient

approvals = ApprovalsClient(
    base_url="https://your-api-host",
    api_key=os.environ["CHAIRLY_API_KEY"],
    workspace_id="cw_acme",
)

ticket = approvals.request(
    action="stripe.refunds.create",
    params={"charge": "ch_123", "amount": 4200},
    wait_ms=30_000,
)`,
      },

      { kind: 'heading', body: 'Idempotency' },
      {
        kind: 'text',
        body: 'Both clients derive the key from workspace + action + stable JSON of the parameters, so a retry of the same logical call rejoins its original approval rather than queueing a second one. Pass your own key when you genuinely want two identical actions to queue separately.',
      },
      {
        kind: 'note',
        tone: 'warn',
        body: 'A caller-supplied key may contain any characters — it is hashed before use, so slashes and long values are safe.',
      },

      { kind: 'heading', body: 'Limits' },
      {
        kind: 'list',
        items: [
          'Inline wait is capped at 60 seconds. Past that you get a pending ticket and poll with the resume token.',
          'The poll loop runs at one second. A blocked workflow costs roughly one read per second while it waits.',
          'Approvals count against your plan’s monthly allowance. Going over bills as overage but is never refused.',
        ],
      },

      { kind: 'heading', body: 'Connectors' },
      {
        kind: 'text',
        body: 'The tools an action can target: Stripe, HubSpot, Shopify, Gmail, Slack, Notion, Google Calendar, Google Sheets, Analytics and QuickBooks, plus agent sources n8n, Make, Lindy, Airtable, CrewAI and a custom webhook.',
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'An action can name a tool that is not connected. The classifier still produces a preview and policy still routes it; execution is what fails, and it fails as a recorded failure rather than silently.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'running-an-agency',
    title: 'Running an agency',
    summary: 'The operational surface: what to watch, what runs on a schedule, and what to configure on the server.',
    section: 'operate',
    keywords: ['admin', 'operations', 'monitoring', 'dashboard', 'settings', 'management'],
    blocks: [
      {
        kind: 'note',
        tone: 'warn',
        body: 'There is no platform administrator. No account can see across agencies, suspend a tenant, or read another agency’s data. The agency dashboard is the highest privilege that exists, and it is scoped to one agency. Cross-tenant operations require direct database access.',
      },

      { kind: 'heading', body: 'What the dashboard gives you' },
      {
        kind: 'table',
        headers: ['Panel', 'What it shows'],
        rows: [
          ['Header', 'Workspaces used against your cap, approvals this period, overage if any, current plan.'],
          ['Waiting on you', 'Every pending action across every client, oldest first.'],
          ['Approval gateway', 'Your API keys: prefix, last used, and revocation.'],
          ['AI provider', 'Which model translates actions into plain language, and your own key if you bring one.'],
          ['Clients', 'One card per client: pending count, connected tools, and whether anything needs attention.'],
          ['White label', 'Branding, subdomain, custom domain and DNS state.'],
        ],
      },

      { kind: 'heading', body: 'Managing clients' },
      {
        kind: 'list',
        items: [
          'Add a client with a name and contact address. Refused with a 402 once you hit your plan cap.',
          'Edit name, contact, status and the full approval policy from Settings on the card.',
          'Pause a client to refuse incoming actions without deleting anything.',
          'Archive a client to keep the audit trail while freeing the billable slot.',
        ],
      },

      { kind: 'heading', body: 'Scheduled work' },
      {
        kind: 'table',
        headers: ['Job', 'Interval', 'What it does'],
        rows: [
          ['Approval expiry sweep', 'Every 60s, jittered', 'Declines pending approvals past their deadline. Always a safe reject.'],
          ['Monthly report delivery', 'Daily, acts on the 1st', 'Emails the previous month’s report per client to your support address.'],
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'Both jobs jitter their first tick and guard against overlapping runs, so several server instances do not all wake at once or stack work on a slow run.',
      },

      { kind: 'heading', body: 'Server configuration' },
      {
        kind: 'table',
        headers: ['Variable', 'Needed for', 'If unset'],
        rows: [
          ['RESEND_API_KEY', 'All outbound email', 'Mail is logged, not sent. Startup warns loudly in production.'],
          ['EMAIL_FROM', 'Default sender address', 'Falls back to a Chairly address.'],
          ['CREDENTIAL_ENCRYPTION_KEY', 'Encrypting OAuth tokens and BYOK keys', 'Throws on use. Must be 64 hex characters.'],
          ['CLOUDFLARE_API_TOKEN, CLOUDFLARE_ZONE_ID', 'Custom domain verification', 'Domains stay at pending_dns and are never served.'],
          ['STRIPE_* / LEMONSQUEEZY_*', 'Billing webhooks', 'Subscription changes are not reconciled.'],
          ['CHAIRLY_INGRESS_KEY', 'Legacy platform-wide gateway key', 'Deprecated. It identifies no agency, so requests behind it are unscoped.'],
          ['DEMO_ACCOUNT_EMAILS', 'Demo accounts with every entitlement', 'Off. Leave empty anywhere real customers exist.'],
        ],
      },
      {
        kind: 'note',
        tone: 'danger',
        body: 'An address listed in DEMO_ACCOUNT_EMAILS bypasses every paywall and has its plan reasserted on each sign-in, so it can never be billed correctly. Never set it on a production deployment.',
      },

      { kind: 'heading', body: 'Logs' },
      {
        kind: 'text',
        body: 'Application logs go to stdout and are read through your host — Cloud Run in the reference deployment. The audit log is a separate, durable, per-client record and is described on its own page.',
      },
      {
        kind: 'todo',
        body: 'There is no in-product log viewer, no user-management screen, and no platform admin surface. Everything above is either dashboard-visible or a server concern.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'errors',
    title: 'Error reference',
    summary: 'Every error code the API returns, why it happens, and what to do about it.',
    section: 'reference',
    keywords: ['errors', 'troubleshooting', 'codes', 'debug', 'failure', '401', '402', '404'],
    blocks: [
      {
        kind: 'text',
        body: 'Errors come back as JSON with a stable `error` code and a human-readable `message`. Match on the code; the message may change.',
      },

      { kind: 'heading', body: 'Authentication and access' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'unauthorized',
            cause: 'Missing or invalid credential — a Firebase token, a gateway key, or a portal session, depending on the route.',
            fix: 'On /v1 routes, check the x-chairly-key header. In production a missing key is refused outright.',
          },
          {
            code: 'agency_not_found',
            cause: 'The signed-in account has no agency record.',
            fix: 'Should be impossible for accounts created after signup provisioning. Older accounts are migrated on sign-in.',
          },
          {
            code: 'not_your_workspace',
            cause: 'A voice decision was attempted on an approval belonging to another agency.',
            fix: 'Nothing to fix. The scope check is working.',
          },
          {
            code: 'session_expired',
            cause: 'A client portal session passed its two-week life.',
            fix: 'Request a new sign-in link.',
          },
          {
            code: 'invalid_link',
            cause: 'A magic link was reused or has expired. They are single-use and last one hour.',
            fix: 'Ask the agency for a fresh invite.',
          },
        ],
      },

      { kind: 'heading', body: 'Approvals' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'approval_not_found',
            cause: 'Unknown id, or an approval belonging to another agency.',
            fix: 'The two are deliberately indistinguishable. Confirm the id came from your own agency.',
          },
          {
            code: 'approval_not_pending',
            cause: 'A decision was made on something already decided or expired.',
            fix: 'Refresh. The first decision stands.',
          },
          {
            code: 'approval_terminal',
            cause: 'A change was attempted on a rejected, expired, executed or failed request.',
            fix: 'Terminal states never change. Submit a new action.',
          },
          {
            code: 'approver_role_not_permitted',
            cause: 'The role may not sign this action — usually an agency user on a client-routed action.',
            fix: 'The client approves it in their portal. The attempt is recorded in the audit log.',
          },
          {
            code: 'approval_illegal_transition',
            cause: 'A state change outside the allowed lifecycle.',
            fix: 'Indicates a client bug rather than a user error.',
          },
          {
            code: 'client_workspace_not_found',
            cause: 'The workspace does not exist, or is not yours.',
            fix: 'Check the id. A wrong-tenant workspace looks identical to a missing one, by design.',
          },
          {
            code: 'client_workspace_not_active',
            cause: 'The client is paused or archived.',
            fix: 'Set the client back to active in the workspace editor.',
          },
        ],
      },

      { kind: 'heading', body: 'Plans and limits' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'client_workspace_limit',
            cause: 'Your plan’s client cap is full.',
            fix: 'Archive a client you no longer serve, or upgrade. Upgrading is not yet possible in-product.',
          },
          {
            code: 'client_logins_not_included',
            cause: 'Client portal logins start on the Agency plan.',
            fix: 'Upgrade. Not yet possible in-product.',
          },
          {
            code: 'branding_level_required',
            cause: 'A white-label field above your plan’s level was submitted.',
            fix: 'Leave it alone or upgrade. Locked fields name the plan that unlocks them.',
          },
          {
            code: 'price_unconfigured',
            cause: 'No price id is configured for that tier and interval.',
            fix: 'A server configuration issue. Set the relevant STRIPE_PRICE_* variable.',
          },
        ],
      },

      { kind: 'heading', body: 'Server and infrastructure' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'gateway_unavailable',
            cause: 'The API key could not be verified because the database was unreachable.',
            fix: 'Retry. An unverifiable key is never treated as valid.',
          },
          {
            code: 'internal_error',
            cause: 'An unhandled failure. Logged server-side with a stack trace.',
            fix: 'Check the server logs for the same moment.',
          },
          {
            code: 'invite_failed',
            cause: 'The invite link could not be created.',
            fix: 'Distinct from the email failing to send, which reports sent: false with an explanation.',
          },
          {
            code: 'connector_unavailable',
            cause: 'The target tool is not connected, or its credentials are rejected.',
            fix: 'Reconnect it under Connectors.',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'troubleshooting',
    title: 'Troubleshooting',
    summary: 'Symptoms rather than codes — what to check when something looks wrong but nothing errored.',
    section: 'reference',
    keywords: ['troubleshooting', 'not working', 'debug', 'help', 'broken', 'stuck'],
    blocks: [
      { kind: 'heading', body: 'My client never received their invite' },
      {
        kind: 'steps',
        items: [
          'Check the result message. It says explicitly whether the email was sent.',
          'If it says email is not configured, set RESEND_API_KEY on the server. Startup warns about this too.',
          'Confirm the client’s contact address on the workspace is correct.',
          'The link is never shown to you — that is deliberate, not a bug.',
        ],
      },

      { kind: 'heading', body: 'My workflow hangs and never gets a decision' },
      {
        kind: 'steps',
        items: [
          'Check who the action was routed to. Client-routed actions wait on the client, not on you.',
          'Check the workspace’s expiry setting. Past it, the action is declined automatically.',
          'An inline wait is capped at 60 seconds; after that you must poll with the resume token.',
        ],
      },

      { kind: 'heading', body: 'Actions run without asking anyone' },
      {
        kind: 'text',
        body: 'The policy lane for that action class is set to run automatically. Check the workspace editor — reads default to automatic, and a per-tool override may be loosening one specific operation.',
      },

      { kind: 'heading', body: 'My custom domain never goes active' },
      {
        kind: 'steps',
        items: [
          'Press "Check DNS now"; the status only moves when something asks.',
          'Confirm every record shown has been added with your DNS provider.',
          'If the status never changes, the server may have no Cloudflare credentials, in which case verification cannot happen at all.',
        ],
      },

      { kind: 'heading', body: 'The monthly report never arrives' },
      {
        kind: 'list',
        items: [
          'It sends on the 1st only, covering the previous month.',
          'It needs a support email set on your branding. Without one the run is skipped.',
          'It needs an email provider configured on the server.',
        ],
      },

      { kind: 'heading', body: 'A retry created a second approval' },
      {
        kind: 'text',
        body: 'The idempotency key differed. Both SDKs derive it from the action and its parameters, so a genuinely identical retry collapses. If you are calling the HTTP API directly, you are responsible for sending a stable key.',
      },

      { kind: 'heading', body: 'I cannot approve something in my own queue' },
      {
        kind: 'text',
        body: 'It is routed to the client. No agency role can sign it, including the owner. Change the workspace policy if future actions of that class should come to you instead — it will not affect the one already waiting.',
      },
    ],
  },
];
